import { useMemo } from "react";
import type { ActorRole, RequestDetail, TaskAction } from "@gw/shared-types";
import { initials } from "@/lib/format";

interface Participant {
  name: string;
  role: ActorRole;
  actions: number;
  waitBd: number;
  waitCd: number;
  activeBd: number;
  activeCd: number;
}

const ROLE_ORDER: Record<ActorRole, number> = {
  ClientService: 0,
  AccountManager: 1,
  Internal: 2,
  Other: 3,
  Consultant: 4,
  SubvendorWorker: 5,
  SubvendorOrg: 6,
};

/** Pick the most-human display name from a person's actions — prefer one without "@", since
 *  some action rows put the email in the actorName field. Falls back to the first non-empty
 *  value. */
function pickDisplayName(actions: TaskAction[]): string {
  for (const a of actions) {
    if (a.actorName && !a.actorName.includes("@")) return a.actorName;
  }
  return actions.find((a) => a.actorName)?.actorName ?? "";
}

/** When Phoenix logs the same person under different roles across actions, pick the
 *  highest-priority role (per ROLE_ORDER — ClientService > AccountManager > Internal >
 *  Consultant). Avoids the "person appears in two roles" duplication and shows the role
 *  most relevant to the workflow. */
function pickPrimaryRole(actions: TaskAction[]): ActorRole {
  let best: ActorRole = actions[0]?.actorRole ?? "Consultant";
  let bestRank = ROLE_ORDER[best];
  for (const a of actions) {
    const rank = ROLE_ORDER[a.actorRole];
    if (rank < bestRank) {
      bestRank = rank;
      best = a.actorRole;
    }
  }
  return best;
}

function deriveParticipants(request: RequestDetail): Participant[] {
  // Dedup by email (case-insensitive) when present — emails are unique identifiers. Fall back
  // to name+role for action rows that lack an email (legacy data, system actors).
  const actionsByKey = new Map<string, TaskAction[]>();
  for (const t of request.tasks) {
    for (const a of t.actions) {
      const email = a.actorEmail?.trim().toLowerCase();
      const key = email && email.length > 0 ? `email:${email}` : `nameRole:${a.actorName}|${a.actorRole}`;
      const bucket = actionsByKey.get(key) ?? [];
      bucket.push(a);
      actionsByKey.set(key, bucket);
    }
  }

  const result: Participant[] = [];
  for (const [, actions] of actionsByKey) {
    result.push({
      name: pickDisplayName(actions),
      role: pickPrimaryRole(actions),
      actions: actions.length,
      waitBd: actions.reduce((sum, a) => sum + (a.waitBd || 0), 0),
      waitCd: actions.reduce((sum, a) => sum + (a.waitCd || 0), 0),
      activeBd: actions.reduce((sum, a) => sum + (a.activeBd || 0), 0),
      activeCd: actions.reduce((sum, a) => sum + (a.activeCd || 0), 0),
    });
  }

  return result.sort(
    (a, b) => (ROLE_ORDER[a.role] - ROLE_ORDER[b.role]) || b.activeBd - a.activeBd,
  );
}

interface Props {
  request: RequestDetail;
}

export function ParticipantsSidebar({ request }: Props) {
  const participants = useMemo(() => deriveParticipants(request), [request]);

  return (
    <div className="section">
      <h3>
        Participants <span className="sub">{participants.length}</span>
      </h3>
      <div className="participants">
        {participants.map((p, i) => (
          <div key={i} className="participant">
            <div className="participant-head">
              <div className="avatar">{initials(p.name)}</div>
              <div className="participant-id">
                <div className="name">{p.name}</div>
                <span className={"actor-role-pill " + p.role.toLowerCase()}>{p.role}</span>
              </div>
              <div className="participant-count">
                <span className="n">{p.actions}</span>
                <span className="lbl">actions</span>
              </div>
            </div>
            <div className="participant-times">
              <div className="time-row">
                <span className="lbl">wait</span>
                <span className="val">
                  {p.waitBd.toFixed(2)} bd · {p.waitCd.toFixed(2)} cd
                </span>
              </div>
              <div className="time-row">
                <span className="lbl">execution</span>
                <span className="val">
                  {p.activeBd.toFixed(2)} bd · {p.activeCd.toFixed(2)} cd
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
